import type { ComponentProps, FormEvent, SubmitEvent } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import { toast } from "@/v2-ui/toast"

export type FormProps = Omit<
  ComponentProps<"form">,
  "noValidate" | "onSubmit"
> & {
  // React 19 narrows form submit events to `SubmitEvent<HTMLFormElement>`,
  // which is what the rest of the codebase declares for form handlers. We
  // cast on the way through so consumers can keep the narrow type.
  onSubmit?(e: SubmitEvent<HTMLFormElement>): void
}

// Lightweight HTML form with built-in constraint-validation handling.
//
// Wraps a `<form noValidate>`. On submit, it runs the native
// `form.checkValidity()` first; if anything is invalid it surfaces the issue
// uniformly (toast + scrollIntoView + focus the first invalid field) and
// blocks submission. Any field with a standard validity API contributes —
// so consumers get error reporting from `<input required>` for free, and
// non-input widgets can opt in by rendering a hidden required input.
//
// Browsers' `:user-invalid` pseudo-class only triggers after a submit attempt
// or user interaction, so styling reacts naturally without bookkeeping here.
function Form(props: FormProps) {
  const { onSubmit, className, children, ...formProps } = props

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    const form = e.currentTarget
    if (!form.checkValidity()) {
      e.preventDefault()
      reportFirstInvalid(form)
      return
    }
    onSubmit?.(e as SubmitEvent<HTMLFormElement>)
  }

  return (
    <form
      noValidate
      onSubmit={handleSubmit}
      className={cn("form", className)}
      {...formProps}
    >
      {children}
    </form>
  )
}

function reportFirstInvalid(form: HTMLFormElement) {
  const invalid = form.querySelector<HTMLElement>(":invalid")
  if (!invalid) return

  const focusTarget = findFocusTarget(invalid)
  focusTarget.scrollIntoView({ block: "center", behavior: "smooth" })
  focusTarget.focus({ preventScroll: true })

  const label = getFieldLabel(invalid)
  const fallback = getValidationMessage(invalid)
  toast({
    variant: "destructive",
    title: "Missing required field",
    description: label
      ? `Please complete: ${label}`
      : (fallback ?? "Please fill in all required fields."),
  })
}

// Hidden / `aria-hidden` validity-bearing inputs are useful for non-input
// widgets (pickers, custom selects), but they're not usable focus targets
// for the user. Walk to a sibling visible interactive element if there is
// one so the focus lands somewhere actionable.
function findFocusTarget(field: HTMLElement): HTMLElement {
  if (field.getAttribute("aria-hidden") !== "true" && field.tabIndex !== -1) {
    return field
  }
  const wrapper = field.parentElement
  if (!wrapper) return field
  const visible = wrapper.querySelector<HTMLElement>(
    'button:not([aria-hidden="true"]):not([tabindex="-1"]),' +
      ' input:not([aria-hidden="true"]):not([tabindex="-1"]):not([type="hidden"]),' +
      ' select:not([aria-hidden="true"]):not([tabindex="-1"]),' +
      ' textarea:not([aria-hidden="true"]):not([tabindex="-1"]),' +
      ' [tabindex]:not([tabindex="-1"]):not([aria-hidden="true"])',
  )
  return visible ?? field
}

function getFieldLabel(field: HTMLElement): string | undefined {
  const id = field.id
  if (id) {
    const label = document.querySelector<HTMLLabelElement>(
      `label[for="${CSS.escape(id)}"]`,
    )
    const text = label?.textContent?.trim()
    if (text) return stripRequiredMarker(text)
  }

  const wrappingLabel = field.closest("label")
  const wrappingText = wrappingLabel?.textContent?.trim()
  if (wrappingText) return stripRequiredMarker(wrappingText)

  const aria = field.getAttribute("aria-label")?.trim()
  if (aria) return aria

  const labeled = field.closest<HTMLElement>("[data-label]")
  const dataLabel = labeled?.dataset.label?.trim()
  if (dataLabel) return dataLabel

  if ("name" in field) {
    const name = (field as HTMLInputElement).name
    if (name) return name
  }

  return undefined
}

// Strip the trailing " *" / "*" that we conventionally append to required
// labels so the toast reads naturally.
function stripRequiredMarker(text: string): string {
  return text.replace(/\s*\*\s*$/, "")
}

function getValidationMessage(field: HTMLElement): string | undefined {
  if ("validationMessage" in field) {
    const msg = (field as HTMLInputElement).validationMessage
    if (msg) return msg
  }
  return undefined
}

export default Form
